import { useState, useEffect } from 'react';
import { GeoJSON } from '../lib/types';
import { API_CONFIG } from '../lib/apiConfig';
import { fetchWithCache } from '../lib/fetchWithCache';

interface UseIsochroneResult {
  boundary: GeoJSON.Polygon | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Loads a supply shed boundary (drive-time isochrone + 3km forest road buffer)
 * for the given mill and drive time. Fetched at runtime from the S3 bucket
 * that serves as the source of truth for all supply shed geometries.
 */
export const useIsochrone = (
  millCode: string | null,
  driveTimeMinutes: number,
  enabled: boolean = true,
): UseIsochroneResult => {
  const [boundary, setBoundary] = useState<GeoJSON.Polygon | null>(null);
  const [isLoading, setIsLoading] = useState(enabled && millCode !== null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !millCode) {
      setBoundary(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    const url = `${API_CONFIG.s3.baseUrl}/${API_CONFIG.s3.paths.millShape(millCode, driveTimeMinutes)}`;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchWithCache<{ geometry: GeoJSON.Polygon }>(url, r => r.json())
      .then((feature) => {
        if (cancelled) return;
        setBoundary(feature.geometry);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(`Failed to load supply shed: ${err.message}`);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [millCode, driveTimeMinutes, enabled]);

  return { boundary, isLoading, error };
};
